/**
 * Hours on a pay entry — DISPLAY ONLY. Nothing here is a pay input, and
 * nothing here adds hours up: a total comes from the API, which counts each
 * ticket once (api#836, `sumTicketHoursOncePerTicket`).
 *
 * WHICH NUMBER A ROW SHOWS turns on `pay_type`, and the two are different
 * quantities on purpose:
 *
 *   - `hourly`      → `hours_worked`. The hours this row was PAID on (a per-hour
 *                     line's quantity, or a manual entry's hours).
 *   - anything else → `ticket_hours`. The ticket's own span, written by the
 *                     API at save time; NULL when the ticket's hours are unknown
 *                     and on shop/manual entries.
 *
 * A ticket-paid row with no `ticket_hours` renders `—`. It must NOT fall back
 * to `hours_worked`: on a ticket-paid row that column is the stored 0.00 (or
 * null) the payroll engine never filled, and printing it says the hand worked
 * no hours on a ticket they were paid for.
 *
 * A multi-day ticket carries its dates — "291.0 h · Aug 13–25" — so a 291-hour
 * span is never read as one shift. The dates are calendar days and are built
 * locally through parseCalendarDate, never `new Date('YYYY-MM-DD')`, which is
 * UTC midnight and lands a day early west of Greenwich.
 */
import { formatCalendarDate, parseCalendarDate } from './calendar-date';
import { fmtHours } from './display-labels';

export interface PayEntryHoursFields {
  pay_type?: string | null;
  hours_worked?: unknown;
  ticket_hours?: unknown;
  ticket_work_date?: string | null;
  ticket_end_date?: string | null;
}

/**
 * A finite, non-negative number of hours, or null. Absent values are rejected
 * BY IDENTITY before the coercion: Number(null) and Number('') are both 0.
 * `ticket_hours` arrives as a string ("291.00") on the timesheet reads and as
 * a number on /employee/my-pay; both are accepted.
 */
function hoursOf(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** The shared fixed-decimal formatter, one decimal, with the unit. */
function hoursText(n: number): string {
  return `${fmtHours(n, 1)} h`;
}

/**
 * "Aug 13–25", "Aug 28 – Sep 2", or with years when they differ. null unless
 * BOTH days parse and the end is strictly after the start — a one-day ticket
 * has no span to show.
 */
export function ticketSpanText(
  workDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null {
  const start = parseCalendarDate(workDate);
  const end = parseCalendarDate(endDate);
  if (!start || !end || end.getTime() <= start.getTime()) return null;

  if (start.getFullYear() !== end.getFullYear()) {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${formatCalendarDate(workDate, opts)} – ${formatCalendarDate(endDate, opts)}`;
  }
  const startText = formatCalendarDate(workDate, { month: 'short', day: 'numeric' });
  if (start.getMonth() === end.getMonth()) return `${startText}–${end.getDate()}`;
  return `${startText} – ${formatCalendarDate(endDate, { month: 'short', day: 'numeric' })}`;
}

/** One pay entry's hours as a cell reads them. Never empty, never a guessed 0. */
export function payEntryHoursText(entry: PayEntryHoursFields): string {
  if (entry.pay_type === 'hourly') {
    const worked = hoursOf(entry.hours_worked);
    return worked === null ? '—' : hoursText(worked);
  }
  const ticket = hoursOf(entry.ticket_hours);
  if (ticket === null) return '—';
  const span = ticketSpanText(entry.ticket_work_date, entry.ticket_end_date);
  return span ? `${hoursText(ticket)} · ${span}` : hoursText(ticket);
}

/**
 * The API's per-worker ticket-hours total (each ticket once). null total is
 * `—`. When some tickets' hours are unknown the total is partial, and says so
 * rather than reading as complete.
 */
export function ticketHoursTotalText(total: unknown, unresolvedTickets?: number | null): string {
  const n = hoursOf(total);
  const unresolved = typeof unresolvedTickets === 'number' && unresolvedTickets > 0 ? unresolvedTickets : 0;
  const unknownNote = `${unresolved} ticket${unresolved === 1 ? '' : 's'} without hours`;
  if (n === null) return unresolved > 0 ? `— (${unknownNote})` : '—';
  return unresolved > 0 ? `${hoursText(n)} (${unknownNote})` : hoursText(n);
}
